import clsx from 'clsx';
import { DatasetItem, GridRow } from '../../../types/types';
import { useTranslate } from '../../../hooks/context/useTranslate';
import { ReportItemsSection, ReportPageSetup } from '../types';
import { formatPrintValue } from '../utils/formatPrintValue';
import { normalizeColumnWidths } from '../utils/normalizeColumnWidths';
import { resolvePrintColumns } from '../utils/resolvePrintColumns';
import { resolveTextStyle } from '../utils/resolveTextStyle';

interface ReportItemsTableProps {
  section: ReportItemsSection;
  items: DatasetItem[];
  rows: GridRow[];
  page: ReportPageSetup;
  /**
   * Off for a sheet that legitimately carries no rows (a footer-only last
   * sheet); the report as a whole does have data.
   */
  showEmptyNotice?: boolean;
}

/**
 * The items table for one sheet.
 *
 * A real <table> with a <thead>, not positioned boxes: the headings then repeat
 * per sheet both on screen and in print. Column widths arrive as percentages of
 * the printable area, so the same template holds up on any paper size.
 */
const ReportItemsTable = ({
  section,
  items,
  rows,
  page,
  showEmptyNotice = true,
}: ReportItemsTableProps) => {
  const { t, language } = useTranslate();
  const columns = resolvePrintColumns(section.columns, items);
  const { percents, tablePercent } = normalizeColumnWidths(
    section.columns,
    page
  );
  const showVerticalLines = section.gridLines?.vertical !== false;
  const showHorizontalLines = section.gridLines?.horizontal !== false;

  if (columns.length === 0) {
    return showEmptyNotice ? (
      <p className="report-empty">{t('print.noData')}</p>
    ) : null;
  }

  return (
    <>
      <table
        className={clsx(
          'report-items-table',
          showVerticalLines && 'report-items-table--vertical-lines',
          showHorizontalLines && 'report-items-table--horizontal-lines'
        )}
        style={{ width: `${tablePercent}%` }}
      >
        <colgroup>
          {columns.map((column, index) => (
            <col
              key={`${column.name}:${index}`}
              style={{ width: `${percents[index] ?? 0}%` }}
            />
          ))}
        </colgroup>
        {section.repeatHeaderOnEachPage !== false && (
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={`${column.name}:${index}`}
                  className={`report-cell-${column.align}`}
                  scope="col"
                  style={resolveTextStyle(section.columns[index]?.style)}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((column, index) => (
                <td
                  key={`${column.name}:${index}`}
                  className={`report-cell-${column.align}`}
                  style={resolveTextStyle(section.columns[index]?.style)}
                >
                  {formatPrintValue(row, column, language)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && showEmptyNotice && (
        <p className="report-empty">{t('print.noData')}</p>
      )}
    </>
  );
};

export default ReportItemsTable;
